import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Banner sutil que aparece cuando Chrome/Edge exponen `beforeinstallprompt`.
 * Al aceptar, dispara el prompt nativo del navegador ("Instalar app / Añadir a pantalla de inicio").
 * En iOS Safari se muestra un texto guía porque no expone la API.
 */
export default function InstallPWAPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);
  const dismissedKey = "connect-pwa-dismissed-v1";

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(dismissedKey)) return;
    // Ya instalada?
    if (window.matchMedia?.("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone) return;

    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    if (isIos) {
      setIos(true);
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    setVisible(false);
    localStorage.setItem(dismissedKey, "1");
  };
  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(dismissedKey, "1");
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-3 left-3 right-3 md:left-auto md:right-4 md:bottom-4 md:max-w-sm z-[9998] rounded-2xl border-2 border-primary/60 bg-card/95 backdrop-blur-md shadow-2xl p-3 pl-4 flex items-center gap-3"
      data-testid="pwa-install-banner"
    >
      <div className="h-10 w-10 rounded-xl bg-primary/20 grid place-items-center shrink-0 border-2 border-primary/50">
        <Download size={18} className="text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold leading-tight">Instalar Connect POS</div>
        <div className="text-[11px] font-semibold text-muted-foreground leading-tight mt-0.5">
          {ios
            ? "En iOS: pulsa Compartir → Añadir a pantalla de inicio."
            : "Guarda la app en tu pantalla para vender más rápido."}
        </div>
      </div>
      {!ios && (
        <Button size="sm" onClick={install} data-testid="pwa-install-button" className="shrink-0">
          Instalar
        </Button>
      )}
      <button
        type="button"
        onClick={dismiss}
        className="p-1 rounded-md hover:bg-secondary/60 text-muted-foreground shrink-0"
        aria-label="Cerrar"
        data-testid="pwa-install-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}
